// The one place a browser's error name becomes a `CaptureError`. Everything
// else in the capture half switches on `kind`; only this function matches
// strings, so a browser that names a failure differently is fixed here.
import type { CaptureError } from "../contract";

type KindOf<K extends CaptureError["kind"]> = Extract<
  CaptureError,
  { kind: K }
>;

// A `getUserMedia` refusal that parks capture in `blocked`: retrying the tap
// will not help until something outside the page changes.
export type PermissionLikeError = KindOf<
  "permission-denied" | "no-microphone" | "microphone-busy"
>;

// Every way `getUserMedia` can fail, as the capture machine sees it.
export type StartFailure = PermissionLikeError | KindOf<"recorder-failed">;

// `DOMException.name` for a rejected `getUserMedia` call (the Media Capture
// and Streams spec's names). Takes the caught value, not a `DOMException`,
// because what a promise rejects with is `unknown`: older WebKit rejected
// with plain `Error`s carrying the same `name`, and a bad constraint dict is
// a `TypeError`.
export const startFailureFromError = (error: unknown): StartFailure => {
  const name =
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    typeof error.name === "string"
      ? error.name
      : "";
  switch (name) {
    // The user or a permissions policy refused; SecurityError is the
    // policy/iframe form of the same refusal.
    case "NotAllowedError":
    case "SecurityError":
    // Pre-standard names still seen from older WebKit and Firefox.
    case "PermissionDeniedError":
      return { kind: "permission-denied" };
    // No input device, or none that satisfies the constraints — on a cheap
    // Chromebook mic the latter is the same fact to an adult.
    case "NotFoundError":
    case "OverconstrainedError":
    case "DevicesNotFoundError":
      return { kind: "no-microphone" };
    // The device exists but the OS or another app holds it. AbortError is
    // the spec's "something other than the above prevented use of the
    // device", which in practice is the same situation.
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return { kind: "microphone-busy" };
    // Unrecognized: nothing says an adult can fix it, so offer another try
    // rather than parking the child in `blocked`.
    default:
      return { kind: "recorder-failed" };
  }
};
